import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { DEMO_QA, getDemoQA } from "@/data/demoQA";
import QAStickyConverter from "@/components/QAStickyConverter";
import FreeTenantHelp from "@/components/FreeTenantHelp";
import { ArrowLeft, ArrowRight, Sparkles, Share2, AlertCircle, MessageCircle } from "lucide-react";
import ShareRow from "@/components/ShareRow";
import FacebookGroupShareButton from "@/components/FacebookGroupShareButton";

/**
 * QASnippet. public, shareable, indexable demo Q&A page.
 *
 * Route: /q/:slug
 *
 * Data lookup order:
 * 1. Static seed entries (instant render, no network)
 * 2. DB-stored admin-published entries via GET /api/qa/:slug
 *
 * Normalises both source shapes (camelCase metaDescription vs snake_case
 * meta_description) into a single `qa` object before render.
 */
export default function QASnippet() {
 const { slug } = useParams();
 const navigate = useNavigate();
 const staticQA = getDemoQA(slug);
 const [qa, setQA] = useState(staticQA ? normalise(staticQA) : null);
 const [checked, setChecked] = useState(!!staticQA);
 const [related, setRelated] = useState([]);

 // "Renters also asked": pull the published library, merge with static
 // seeds, prefer same-state questions, exclude the current one. Pure DB
 // read, zero AI cost, keeps readers moving through the library.
 useEffect(() => {
 if (!qa) return;
 let alive = true;
 (async () => {
 let published = [];
 try {
 const { data } = await api.get(`/qa/list`);
 published = data.items || [];
 } catch {/* library still renders from static seeds */}
 if (!alive) return;
 const seen = new Set([slug]);
 const pool = [...published, ...DEMO_QA].filter(q => q.slug && !seen.has(q.slug) && seen.add(q.slug));
 const generic = (s) => !s || s === "ALL" || s === "AU";
 const same = pool.filter(q => q.state === qa.state && !generic(qa.state));
 const wide = pool.filter(q => generic(q.state) && q.state !== qa.state);
 const rest = pool.filter(q => !same.includes(q) && !wide.includes(q));
 setRelated([...same, ...wide, ...rest].slice(0, 3));
 })();
 return () => { alive = false; };
 }, [slug, qa]);

 // If not in the static set, try the API. First the curated Q&A library,
 // then the legacy /brain/share endpoint (user-shared Brain Q&As). 404 if
 // neither matches.
 useEffect(() => {
 if (staticQA) return;
 let alive = true;
 (async () => {
 try {
 const { data } = await api.get(`/qa/${slug}`);
 if (alive) setQA(normalise(data));
 return;
 } catch (e) {
 if (e.response?.status !== 404) {
 if (alive) setQA(null);
 return;
 }
 }
 // Fallback: legacy Brain-share snapshot
 try {
 const { data } = await api.get(`/brain/share/${slug}`);
 if (alive) setQA(normaliseBrainShare(data));
 } catch {
 if (alive) setQA(null);
 } finally {
 if (alive) setChecked(true);
 }
 })().finally(() => { if (alive) setChecked(true); });
 return () => { alive = false; };
 }, [slug, staticQA]);

 // Manage <title>, meta description, OpenGraph + Twitter Card meta, and the
 // JSON-LD QAPage structured-data block. All cleaned up on unmount so we
 // don't leak per-page values into other routes.
 useEffect(() => {
 if (!qa) return;
 const prevTitle = document.title;
 document.title = `${qa.title} · TenantTrail`;

 const pageUrl = typeof window !== "undefined" ? window.location.href : "";
 const plainAnswer = markdownToPlain(qa.answer);

 // <meta name="description">
 const descMeta = upsertMeta({ name: "description" }, qa.metaDescription);

 // OpenGraph. controls how the page looks when shared on Facebook / iMessage
 const ogTitle = upsertMeta({ property: "og:title" }, qa.title);
 const ogDesc = upsertMeta({ property: "og:description" }, qa.metaDescription);
 const ogType = upsertMeta({ property: "og:type" }, "article");
 const ogUrl = upsertMeta({ property: "og:url" }, pageUrl);
 const ogSite = upsertMeta({ property: "og:site_name" }, "TenantTrail");

 // Twitter Card. controls how the page looks when shared on X / Twitter
 const twCard = upsertMeta({ name: "twitter:card" }, "summary_large_image");
 const twTitle = upsertMeta({ name: "twitter:title" }, qa.title);
 const twDesc = upsertMeta({ name: "twitter:description" }, qa.metaDescription);

 // JSON-LD QAPage structured data. gets us "People also ask" rich snippets
 // in Google search results. Schema.org spec: https://schema.org/QAPage
 const ld = {
 "@context": "https://schema.org",
 "@type": "QAPage",
 "mainEntity": {
 "@type": "Question",
 "name": qa.title,
 "text": qa.question,
 "answerCount": 1,
 "acceptedAnswer": {
 "@type": "Answer",
 "text": plainAnswer,
 "author": { "@type": "Organization", "name": "TenantTrail · Compass AI" },
 "url": pageUrl,
 },
 },
 };
 const ldScript = document.createElement("script");
 ldScript.type = "application/ld+json";
 ldScript.id = "tt-qa-jsonld";
 ldScript.textContent = JSON.stringify(ld);
 document.head.appendChild(ldScript);

 return () => {
 document.title = prevTitle;
 [descMeta, ogTitle, ogDesc, ogType, ogUrl, ogSite, twCard, twTitle, twDesc].forEach(m => m?.restore?.());
 document.getElementById("tt-qa-jsonld")?.remove();
 };
 }, [qa]);

 if (!qa && checked) {
 return (
 <div className="min-h-screen paper-grain">
 <Navbar />
 <div className="max-w-3xl mx-auto px-6 py-16 text-center">
 <AlertCircle size={28} strokeWidth={1.5} className="mx-auto text-[#B35D4C] mb-3" />
 <h1 className="font-serif text-3xl text-[#0D2B52] mb-2">Question not found</h1>
 <p className="text-[#6B7280] mb-6">This Q&A may have moved. Browse the full library:</p>
 <Link to="/q" className="btn-primary inline-block" data-testid="qa-back-to-index">All questions</Link>
 </div>
 </div>
 );
 }
 if (!qa) return null; // still loading

 const shareUrl = typeof window !== "undefined" ? window.location.href : "";

 const onShare = async () => {
 try {
 if (navigator.share) {
 await navigator.share({ title: qa.title, text: qa.teaser, url: shareUrl });
 } else if (navigator.clipboard) {
 await navigator.clipboard.writeText(shareUrl);
 alert("Link copied. paste it anywhere.");
 }
 } catch {/* user cancelled. no-op */}
 };

 // Convert markdown-ish answer to React. Keep it simple. split on headings
 // and paragraphs, render markdown links as <a>, bold as <strong>.
 const renderAnswer = (text) => {
 const lines = text.split("\n");
 return lines.map((line, i) => {
 if (!line.trim()) return <div key={i} className="h-3" />;
 if (line.startsWith("### ")) {
 return <h3 key={i} className="font-serif text-lg text-[#0D2B52] mt-6 mb-2">{line.slice(4)}</h3>;
 }
 if (line.startsWith("## ")) {
 return <h2 key={i} className="font-serif text-xl text-[#0D2B52] mt-7 mb-3">{line.slice(3)}</h2>;
 }
 if (line.startsWith("> ")) {
 return (
 <blockquote key={i} className="border-l-4 border-[#1B6C62] pl-4 py-2 my-3 italic text-[#1A1A1A] bg-[#FAF9F6]">
 {renderInline(line.slice(2))}
 </blockquote>
 );
 }
 if (line.startsWith("- ") || line.startsWith("* ")) {
 return (
 <li key={i} className="ml-6 list-disc text-[#1A1A1A] leading-relaxed my-1">
 {renderInline(line.slice(2))}
 </li>
 );
 }
 return (
 <p key={i} className="text-[#1A1A1A] leading-relaxed mb-2">
 {renderInline(line)}
 </p>
 );
 });
 };

 const renderInline = (text) => {
 // Very small parser: **bold** + _italic_ + \. escape for periods
 const parts = [];
 const re = /(\*\*[^*]+\*\*)/g;
 let last = 0;
 let m;
 while ((m = re.exec(text)) !== null) {
 if (m.index > last) parts.push(text.slice(last, m.index));
 parts.push(<strong key={parts.length} className="text-[#0D2B52]">{m[1].slice(2, -2)}</strong>);
 last = m.index + m[1].length;
 }
 if (last < text.length) parts.push(text.slice(last));
 return parts;
 };

 return (
 <div className="min-h-screen paper-grain" data-testid="qa-snippet-page">
 <Navbar />
 <article className="max-w-3xl mx-auto px-6 sm:px-8 py-12">
 <Link to="/q" className="text-xs text-[#1B6C62] hover:underline inline-flex items-center gap-1 mb-6" data-testid="qa-back-link">
 <ArrowLeft size={12} strokeWidth={1.75} /> All questions
 </Link>

 <ShareRow url={shareUrl} title={qa.title} slug={slug} />

 <div className="bg-[#EFF8F6] border-l-4 border-[#1877F2] p-3 mb-6 flex items-center justify-between gap-3 flex-wrap">
   <div className="text-xs text-[#0D2B52] leading-relaxed max-w-md">
     <strong>Help another renter find this answer.</strong>{" "}
     Share it in the TenantTrail Facebook group. tens of thousands of Aussie tenants ask similar questions every month.
   </div>
   <FacebookGroupShareButton
     url={shareUrl}
     title={qa.title}
     kind="q-and-a"
     slug={slug}
     hint={false}
   />
 </div>

 <div className="label-mono mb-2 text-[#1B6C62]">{qa.state === "ALL" ? "Australia-wide" : qa.state} · Tenant Q&A</div>
 <h1 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] mb-4 leading-tight" data-testid="qa-title">{qa.title}</h1>
 <p className="text-xs text-[#6B7280] italic mb-8 font-serif">{qa.persona}</p>

 {/* Question card */}
 <div className="card bg-[#FAF9F6] border-l-4 border-l-[#0D2B52] mb-6">
 <div className="label-mono mb-1 text-[#0D2B52]">The question</div>
 <p className="text-[#1A1A1A] font-serif leading-relaxed text-[15px]" data-testid="qa-question">
 {qa.question}
 </p>
 </div>

 {/* Compass answer */}
 <div className="card border-l-4 border-l-[#1B6C62] mb-8" data-testid="qa-answer">
 <div className="flex items-center gap-2 mb-3">
 <Sparkles size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="label-mono text-[#1B6C62]">Compass says</span>
 </div>
 <div className="text-[15px]">{renderAnswer(qa.answer)}</div>
 {qa.citations?.length > 0 && (
 <div className="mt-6 pt-4 border-t border-[#E5E4DE]">
 <div className="label-mono mb-2 text-[#6B7280]">Act sections cited</div>
 <div className="flex flex-wrap gap-1.5">
 {qa.citations.map((c, i) => (
 <span key={i} className="text-[11px] font-mono bg-[#E6F5F2] border border-[#1B6C62]/30 text-[#0D2B52] px-2 py-0.5">{c}</span>
 ))}
 </div>
 </div>
 )}
 </div>

 {/* Disclaimer */}
 <p className="text-[11px] text-[#6B7280] italic font-serif mb-10 leading-relaxed">
 Not legal advice. Compass is an AI assistant. For advice on your specific situation, contact your state&apos;s free Tenants&apos; Union.
 </p>

 {/* Contextual free-help block — only for state-specific Q&As. On
     Australia-wide entries the full grid still lives in the footer. */}
 {qa.state && qa.state !== "ALL" && (
 <FreeTenantHelp variant="single" state={qa.state} />
 )}

 {/* Renters also asked — internal links keep readers in the library */}
 {related.length > 0 && (
 <div className="mb-10" data-testid="qa-related">
 <div className="label-mono mb-3 text-[#0D2B52]">
 {qa.state && qa.state !== "ALL" && qa.state !== "AU" ? `Renters in ${qa.state} also asked` : "Renters also asked"}
 </div>
 <div className="space-y-3">
 {related.map((r) => (
 <Link key={r.slug} to={`/q/${r.slug}`} className="block card card-hover" data-testid={`qa-related-${r.slug}`}>
 <div className="flex items-center gap-2 mb-1">
 <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[#1B6C62] bg-[#E6F5F2] border border-[#1B6C62]/30 px-1.5 py-0.5">
 {(!r.state || r.state === "ALL" || r.state === "AU") ? "AUS-WIDE" : r.state}
 </span>
 </div>
 <h3 className="font-serif text-lg text-[#0D2B52] leading-snug mb-1">{r.title}</h3>
 <p className="text-[13px] text-[#6B7280] leading-relaxed mb-1.5">{r.teaser}</p>
 <span className="inline-flex items-center gap-1 text-[11.5px] font-mono uppercase tracking-[0.1em] text-[#1B6C62]">
 Read the answer <ArrowRight size={11} strokeWidth={2} />
 </span>
 </Link>
 ))}
 </div>
 </div>
 )}

 {/* Big conversion CTA */}
 <div className="card border-2 border-[#1B6C62] bg-[#E6F5F2]/30 mb-10">
 <div className="label-mono mb-2 text-[#1B6C62]">Your situation is different. Try Compass for free</div>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Get a personalised answer in under 60 seconds</h2>
 <p className="text-[#1A1A1A] font-serif text-[15px] leading-relaxed mb-5">
 Sign up free and ask Compass about <em>your</em> tenancy. It reads the Residential Tenancies
 Act for your state, drafts letters you can send straight to your agent and builds a
 tribunal-ready evidence bundle if you need it. From AUD$19.99/month, or AUD$49 one-off for
 Dispute Mode if you&apos;re heading to tribunal this month.
 </p>
 <div className="flex flex-wrap gap-3">
 <Link to="/register" className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="qa-signup-cta">
 Start free <ArrowRight size={13} strokeWidth={1.75} />
 </Link>
 <Link to="/pricing" className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="qa-pricing-cta">
 See plans
 </Link>
 <Link to="/ask" className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="qa-brain-cta">
 <MessageCircle size={13} strokeWidth={1.75} /> See what the Brain can solve
 </Link>
 </div>
 </div>

 {/* Share */}
 <div className="flex justify-between items-center pt-6 border-t border-[#E5E4DE]">
 <button
 onClick={onShare}
 className="btn-ghost inline-flex items-center gap-2 text-sm"
 data-testid="qa-share-button"
 >
 <Share2 size={13} strokeWidth={1.75} /> Share this answer
 </button>
 <button
 onClick={() => navigate("/q")}
 className="text-sm text-[#1B6C62] hover:underline"
 data-testid="qa-more-link"
 >
 More questions →
 </button>
 </div>
 </article>
 {/* Sticky converter. slides up once the user has read ~35% of the page */}
 <QAStickyConverter slug={qa.slug} />
 </div>
 );
}

/**
 * Normalise db (snake_case) and static (camelCase) shapes into a single shape
 * so the renderer doesn't need to care about source.
 */
function normalise(raw) {
 return {
 slug: raw.slug,
 state: raw.state,
 title: raw.title,
 teaser: raw.teaser,
 metaDescription: raw.metaDescription || raw.meta_description || "",
 persona: raw.persona || "Australian tenant",
 question: raw.question,
 answer: raw.answer,
 citations: raw.citations || [],
 };
}

/**
 * Brain-share snapshots are minimal. just question/answer/state. Synthesize
 * the other fields so the same renderer works for both curated Q&As and
 * user-shared Brain snapshots.
 */
function normaliseBrainShare(raw) {
 const firstSentence = (raw.answer || "").split(/[.\n]/)[0].slice(0, 130);
 const title = (raw.question || "").length > 75
 ? raw.question.slice(0, 72).trimEnd() + "…"
 : (raw.question || "Tenant question");
 return {
 slug: raw.slug,
 state: raw.state || "ALL",
 title,
 teaser: firstSentence,
 metaDescription: firstSentence.slice(0, 155),
 persona: "Shared via TenantTrail Brain",
 question: raw.question || "",
 answer: raw.answer || "",
 citations: [],
 };
}

/**
 * Create or update a single <meta> tag in <head>. Returns a `restore()`
 * function that puts the tag back to whatever it was before. used in the
 * useEffect cleanup so SPA navigation doesn't leak per-page meta state.
 */
function upsertMeta(attrs, content) {
 const selector = Object.entries(attrs).map(([k, v]) => `[${k}="${v}"]`).join("");
 let el = document.querySelector(`meta${selector}`);
 let created = false;
 let prev = null;
 if (!el) {
 el = document.createElement("meta");
 Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
 document.head.appendChild(el);
 created = true;
 } else {
 prev = el.getAttribute("content");
 }
 el.setAttribute("content", content);
 return {
 restore: () => {
 if (created) el.remove();
 else if (prev != null) el.setAttribute("content", prev);
 },
 };
}

/**
 * Strip markdown formatting for the JSON-LD structured-data answer field.
 * Google accepts HTML or plain text. plain text is safer and ranks the
 * same. Keep this minimal. full markdown parsing isn't necessary.
 */
function markdownToPlain(md) {
 if (!md) return "";
 return md
.replace(/^#{1,6}\s+/gm, "") // strip leading # heading marks
.replace(/\*\*([^*]+)\*\*/g, "$1") // **bold** → bold
.replace(/\*([^*]+)\*/g, "$1") // *italic* → italic
.replace(/_([^_]+)_/g, "$1") // _italic_ → italic
.replace(/`([^`]+)`/g, "$1") // `code` → code
.replace(/^>\s+/gm, "") // strip blockquote >
.replace(/^[-*+]\s+/gm, "• ") // bullets → •
.replace(/\n{3,}/g, "\n\n") // collapse triple newlines
.trim();
}
